import { useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useParams } from "@tanstack/react-router";
import type { Content, StepDoc } from "@/app/queries";
import { Button } from "@/ui/Button";
import { Toast, type ToastTone } from "@/ui/states";
import { count, plural } from "@/lib/plural";
import { AssignmentCard } from "./AssignmentCard";
import { ACCEPT, acceptedExt, defaultRole, FILE_ROLES, MAX_FILE_BYTES, MAX_STEP_BYTES, useStepAssignment, useUploadStepFile, type Assignment, type FileRole } from "./filesApi";
import { CardHead, Chip, fmtSize, IconButton, INPUT, ROLE_LOOK, type Patch } from "./shared";

const HANDOUT = [
  { mark: "✓", fg: "var(--ok)", text: "Шаблон ноутбука копируется каждому студенту в его рабочее пространство" },
  { mark: "✓", fg: "var(--ok)", text: "README и данные подключаются в ту же папку" },
  { mark: "○", fg: "var(--warn)", text: "Эталон и скрытые тесты студенту не видны — открываются после принятой сдачи" },
];

const GRID = "grid grid-cols-[28px_minmax(0,1.6fr)_128px_90px_minmax(0,1fr)_26px] gap-3";
const LIMITS = "при лимите 5 МБ на файл и 10 МБ на шаг";
const UPLOAD_FAILED = "Не удалось загрузить файл";

type Props = { content: Content; patch: (p: Patch) => void; stepId?: string };
type ToastState = { tone: ToastTone; title: string; body: ReactNode; action?: { label: string; onClick: () => void } } | null;
type Staged = { file: File; role: FileRole };

function icon(name: string) {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext === "ipynb" ? "[nb]" : ext === "md" ? "[md]" : ext === "csv" ? "[csv]" : "[f]";
}

const whoSees = (role: string) => (role === "эталон" ? "только менторы" : "студенты когорты");

/** Подпись под именем файла — из задания шага, а не строкой из макета.
 *  a: undefined — задание ещё грузится, null — ноутбука у шага нет. */
function note(f: Content["files"][number], a: Assignment | null | undefined): string {
  switch (f.role) {
    case "шаблон":
      if (a && a.template_blob_id === f.blob_id) return `${count(a.manifest.tasks.length, "задача", "задачи", "задач")} · ноутбук-шаблон`;
      return a === undefined ? "ноутбук-шаблон" : "прежняя версия шаблона";
    case "эталон":
      return "эталонное решение";
    case "данные":
      if (a?.manifest.datasets.some((d) => d.blob_id === f.blob_id)) return "датасет · подключается к ноутбуку";
      return a === null ? "не подключён — загрузите заново после шаблона" : "датасет";
    default:
      return "описание домашки";
  }
}

/** Вкладка «домашка»: файлы шага, автопроверка, выдача студенту.
 *  stepId — из пропса, иначе из маршрута /admin/course/$stepId. */
export function FilesTab({ stepId, ...rest }: Props) {
  return stepId ? <FilesPanel {...rest} stepId={stepId} /> : <FilesFromRoute {...rest} />;
}

function FilesFromRoute(props: Omit<Props, "stepId">) {
  const { stepId } = useParams({ strict: false }) as { stepId?: string };
  if (!stepId) return null;
  return <FilesPanel {...props} stepId={stepId} />;
}

function FilesPanel({ content, patch, stepId }: Omit<Props, "stepId"> & { stepId: string }) {
  const files = content.files;
  const n = files.length;
  const total = files.reduce((a, f) => a + f.size, 0);
  const assignment = useStepAssignment(stepId).data;
  const upload = useUploadStepFile(stepId);
  const inputId = useId();
  const roleId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  useEffect(() => {
    if (!toast || toast.tone === "err") return;
    const id = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(id);
  }, [toast]);

  const reopen = () => fileRef.current?.click();
  const resetInput = () => {
    if (fileRef.current) fileRef.current.value = "";
  };

  const pick = (file: File) => {
    resetInput();
    if (!acceptedExt(file.name)) {
      setStaged(null);
      setToast({ tone: "err", title: UPLOAD_FAILED, body: `${file.name} — такой тип не принимаем: ipynb · md · csv · py · zip. В шаг ничего не добавлено.`, action: { label: "выбрать другой", onClick: reopen } });
      return;
    }
    if (file.size > MAX_FILE_BYTES || total + file.size > MAX_STEP_BYTES) {
      setStaged(null);
      setToast({ tone: "err", title: UPLOAD_FAILED, body: `${file.name} — ${fmtSize(file.size)} ${LIMITS}`, action: { label: "повторить", onClick: reopen } });
      return;
    }
    setToast(null);
    setStaged({ file, role: defaultRole(file.name) });
  };

  const send = async ({ file, role }: Staged) => {
    if (busy) return;
    setBusy(file.name);
    setToast(null);
    try {
      const doc: StepDoc = await upload.mutateAsync({ file, role });
      // Черновик вкладки живёт в автосохранении CoursePage — принимаем файлы и
      // привязку шаблона из ответа сервера, остальное остаётся локальным.
      patch((c) => ({ ...c, files: doc.content.files, homework: { ...c.homework, file_blob_id: doc.content.homework.file_blob_id } }));
      setStaged(null);
      setToast({ tone: "ok", title: "Файл добавлен", body: `${file.name} · ${role} · видят ${whoSees(role)}` });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "нет связи с сервером.";
      setToast({ tone: "err", title: UPLOAD_FAILED, body: `${file.name} — ${fmtSize(file.size)}. ${msg}`, action: { label: "повторить", onClick: () => void send({ file, role }) } });
    } finally {
      setBusy(null);
    }
  };

  const remove = (i: number) =>
    patch((c) => {
      const gone = c.files[i];
      const homework = gone && c.homework.file_blob_id === gone.blob_id ? { ...c.homework, file_blob_id: undefined } : c.homework;
      return { ...c, files: c.files.filter((_, j) => j !== i), homework };
    });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && !busy) pick(f);
  };

  return (
    <div className="flex flex-col gap-3.5">
      <section className="overflow-hidden rounded-md border border-bd bg-card">
        <CardHead
          title="файлы домашки"
          meta="ipynb · md · csv · py · zip"
          right={
            <span className="font-mono text-[11px] text-ink2" data-testid="files-summary">
              {n} {plural(n, "файл", "файла", "файлов")} · {fmtSize(total)}
            </span>
          }
        />
        <div className={`${GRID} border-b border-bd px-3.5 py-2 font-mono text-[10px] uppercase tracking-[.05em] text-mut`}>
          <span />
          <span>файл</span>
          <span>роль</span>
          <span>размер</span>
          <span>кто видит</span>
          <span />
        </div>
        {files.map((f, i) => (
          <div key={f.blob_id || `f-${i}`} className={`${GRID} items-center border-b border-bd px-3.5 py-2.5`} data-testid="file-row">
            <span className="font-mono text-[11px]" style={{ color: f.name.endsWith(".ipynb") ? "var(--acc)" : "var(--mut)" }}>
              {icon(f.name)}
            </span>
            <div className="min-w-0">
              <div className="truncate font-mono text-[12.5px] text-ink">{f.name}</div>
              <div className="mt-0.5 truncate font-mono text-[10.5px] text-mut">{note(f, assignment)}</div>
            </div>
            <Chip look={ROLE_LOOK[f.role] ?? ROLE_LOOK.описание!}>{f.role}</Chip>
            <span className="font-mono text-[11px] text-mut">{fmtSize(f.size)}</span>
            <span className="truncate font-mono text-[11px] text-ink2">{whoSees(f.role)}</span>
            <IconButton onClick={() => remove(i)} aria-label={`убрать файл ${f.name}`}>
              ×
            </IconButton>
          </div>
        ))}
        {n === 0 && <div className="border-b border-bd px-3.5 py-[11px] text-[12.5px] text-ink2">У шага пока нет файлов домашки — студент увидит только практику.</div>}

        <div className="flex flex-col gap-2.5 p-3.5">
          <input
            ref={fileRef}
            id={inputId}
            type="file"
            accept={ACCEPT}
            className="peer sr-only"
            aria-label="файл домашки"
            disabled={!!busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) pick(f);
            }}
          />
          <label
            htmlFor={inputId}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="block w-full cursor-pointer rounded-md border border-dashed border-bd2 p-[18px] text-center font-mono text-[12.5px] leading-[1.6] text-ink2 hover:border-acc peer-focus-visible:border-acc"
            data-testid="dropzone"
          >
            перетащи ноутбук, README или данные
            <br />
            <span className="text-[11px] text-mut">ноутбук-шаблон студент получает как есть, эталон остаётся у менторов</span>
          </label>

          {busy && (
            <div className="rounded-sm border border-bd bg-bg2 px-3 py-2.5 font-mono text-[12px] text-ink2" role="status" data-testid="upload-progress">
              загружаем {busy}…
            </div>
          )}

          {staged && !busy && (
            <div className="flex flex-wrap items-center gap-3 rounded-sm border border-bd2 bg-bg2 px-3 py-2.5" data-testid="upload-staged">
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-[12.5px] text-ink">{staged.file.name}</div>
                <div className="mt-0.5 font-mono text-[11px] text-ink2">
                  {fmtSize(staged.file.size)} · видят {whoSees(staged.role)}
                </div>
              </div>
              <label htmlFor={roleId} className="font-mono text-[11px] text-ink2">
                роль
              </label>
              <select
                id={roleId}
                aria-label="роль файла"
                value={staged.role}
                onChange={(e) => setStaged({ ...staged, role: e.target.value as FileRole })}
                className={`${INPUT} py-1 font-mono text-[11.5px]`}
              >
                {FILE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <Button variant="primary" size="sm" onClick={() => void send(staged)}>
                загрузить
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setStaged(null)}>
                отмена
              </Button>
            </div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3.5">
        <AssignmentCard stepId={stepId} onThreshold={(t) => patch((c) => ({ ...c, homework: { ...c.homework, threshold: t } }))} />
        <section className="rounded-md border border-bd bg-card p-3.5">
          <div className="mb-[11px] font-mono text-[11px] uppercase tracking-[.06em] text-mut">выдача студенту</div>
          <div className="flex flex-col gap-[9px]">
            {HANDOUT.map((h) => (
              <div key={h.text} className="flex items-baseline gap-[9px] text-[12.5px] leading-[1.45] text-ink2">
                <span className="font-mono" style={{ color: h.fg }}>
                  {h.mark}
                </span>
                <span className="text-pretty">{h.text}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-5 right-5 z-30 w-[400px] max-w-[calc(100vw-40px)]" data-testid="files-toast">
          <Toast
            tone={toast.tone}
            title={toast.title}
            body={toast.body}
            action={
              toast.action
                ? {
                    ...toast.action,
                    onClick: () => {
                      setToast(null);
                      toast.action?.onClick();
                    },
                  }
                : { label: "закрыть", onClick: () => setToast(null) }
            }
          />
        </div>
      )}
    </div>
  );
}
